
import Vue from 'vue';
import _keyBy from 'lodash/keyBy';
import _omit from 'lodash/omit';
import moment from 'moment-timezone';
import pluralize from 'pluralize';
import { CompanyCustomFieldDef, DbTimestamp, Team } from '@run-diffusion/shared';
import { mapActions, mapGetters, mapState } from 'vuex';
import { Timestamp } from '@/firebase';
import { CursorListMixin, FiltersPanelMixin, TeamUserMixin } from '@/mixins';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { StoredLastPagination } from '@/store';
import { ListFetchResult, restartWalk, walkPageNumber } from '@/utils/cursorWalk';
import { HydratedListView, hydrateListView, rememberListView } from '@/utils/listView';
import { normalizePanelFilters } from '@/utils/panelFilters';
import { TEAMS_API_BASE_URL, TeamListRow, fetchTeamsPage } from '@/utils/teamsApi';
import { MirrorLagChannel } from '@/utils/mirrorLagChannel';
import { TEAMS_ADDED, noteTeamsChanged } from '@/utils/teamsMirrorLag';
import { fetchTeamsById, withFreshTeams } from '@/services/teamDocs';
import {
	MEMBERSHIP_STATUS_ITEMS,
	MEMBER_TEAMS_RESTING_FILTERS,
	MemberTeamsPanelFilters,
	TEAMS_DEFAULT_OPTIONS,
	TEAMS_HEADER_VALUE,
	TEAM_ACTIVATION,
	TEAM_ACTIVATION_ITEMS,
	TEAM_CUSTOM_FIELD_HEADER_PREFIX,
	TEAM_TABLES_ITEMS_PER_PAGE_OPTIONS,
	TableHeaderValue,
	TeamDownloadRow,
	buildMemberTeamsRequest,
	isSortableTeamsHeader,
	stubTeamFromRow,
	teamDownloadHeaders,
	teamDownloadRow,
	teamTablesDownloadSort,
} from '@/views/Team/teamTablesState';
import TeamMemberTeamRow from './TeamMemberTeamRow.vue';
import TableBulkActionsPopover from '@/components/base/TableBulkActionsPopover.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import EmptyState from '@/components/states/EmptyState.vue';
import LiveDoc from '@/components/LiveDoc.vue';
import TableToolbar from '@/components/Filters/TableToolbar.vue';
import FiltersPanel from '@/components/Filters/FiltersPanel.vue';

/**
 * One member's Teams, the way the Directory lists people: Postgres (through
 * api2's `/teams?member_email=`) picks and orders each page within what the
 * viewer may see, filtered by the search and the Filters box, and every row
 * binds its own Team and stays live (LiveDoc). Pending invitations are listed
 * too unless the Status filter says otherwise. Paging is CursorListMixin's (it
 * also hears the teams mirror-lag channel for additions, so a Team they join
 * anywhere catches up here; an edit stays where it is until Refresh); the box is
 * FiltersPanelMixin's. Row and bulk actions hand TeamPage's dialogs the Teams
 * read fresh. The view is remembered per member.
 *
 * Active or Deactivated is the `?deactivated` route query, as on the company's
 * Teams list, so the box's Activation field writes the route. Every link between
 * the company's pages carries the query, so a member reached from the deactivated
 * Teams lands on their deactivated Teams.
 */
export default Vue.extend({
	name: 'TeamMemberTeamsManagement',
	mixins: [
		TeamUserMixin,
		CursorListMixin,
		FiltersPanelMixin,
	],
	props: {
		currTeamMember: { type: Object, default: null },
		currTeam: { type: Object, default: null },
		teamUserRoleDefs: { type: Array, default: () => [] },
	},
	data () {
		return {
			options: { ...TEAMS_DEFAULT_OPTIONS },
			itemsPerPageOptions: TEAM_TABLES_ITEMS_PER_PAGE_OPTIONS,
			searchQuery: '',
			appliedFilters: { ...MEMBER_TEAMS_RESTING_FILTERS } as MemberTeamsPanelFilters,
			hydratedViewKey: null as string | null,
			// The half the rows on screen were fetched in, which a new Activation outruns until they arrive
			listedActivation: TEAM_ACTIVATION.ACTIVE as string,
			// Kept across pages; a new search, filter or member clears it.
			selectedRows: [] as TeamListRow[],
			loadingBulkTeams: false,
			statusFilterItems: MEMBERSHIP_STATUS_ITEMS,
			activationFilterItems: TEAM_ACTIVATION_ITEMS,
		};
	},
	computed: {
		...mapState([
			'companyDashboard',
			'user',
			'team',
		]),
		...mapGetters([
			'displayLabels',
			'companyClubOfferBenefits',
		]),
		teamCustomFieldDefs (): CompanyCustomFieldDef[] {
			return this.team?.company?.teamCustomFieldDefs || [];
		},
		// Opens with the company's name, as the Directory's does
		downloadFileName (): string {
			const companyName: string = this.team?.company?.name || '';
			const memberName: string = this.currTeamMember ? this.currTeamMember.email.split('@')[0] : '';
			const listName: string = `${memberName}-member-${this.teamLabelPlural}`;
			return companyName ? `${companyName}-${listName}` : listName;
		},
		teamLabelPlural (): string {
			return pluralize(this.displayLabels?.team || 'Team');
		},
		searchPlaceholder (): string {
			const customField: string = this.teamCustomFieldDefs.length ? `, ${this.teamCustomFieldDefs[0].label}` : '';
			return `Search by ${this.displayLabels.team} name${customField}...`;
		},
		isSelfMember (): boolean {
			return !!(this.user && this.currTeamMember && this.user.id === this.currTeamMember.userId);
		},
		canBulkManageTeams (): boolean {
			return !this.scimProvisioningLocksMembership && this.hasManageTeamsPermission;
		},
		// A deactivated Team's roles are neither shown nor changed, as on the Team's own page,
		// so the deactivated half lists no role column and offers no Roles
		showTeamRoleColumn (): boolean {
			return !!(this.companyClubOfferBenefits.isEnterprise && (this.hasManageTeamsPermission || this.isSelfMember) && !this.showingDeactivated);
		},
		canBulkEditRoles (): boolean {
			return this.canBulkManageTeams && !this.showingDeactivated;
		},
		canBulkRemoveTeams (): boolean {
			return this.canBulkManageTeams && !this.isSelfMember;
		},
		// Nothing to offer beside Download: no Roles on deactivated Teams, and no Remove of yourself
		showBulkTeamActions (): boolean {
			return this.canBulkEditRoles || this.canBulkRemoveTeams;
		},
		showingDeactivated (): boolean {
			return !!this.$route.query.deactivated;
		},
		// CursorListMixin: a Team or membership added anywhere in the app
		listChangeChannel (): MirrorLagChannel {
			return TEAMS_ADDED;
		},
		// One view per member of one Team, as the Firestore-era table remembered it
		viewKey (): string | null {
			return this.currTeam?.id && this.currTeamMember?.id ? `${this.currTeam.id}/${this.currTeamMember.id}` : null;
		},
		// FiltersPanelMixin
		restingPanelFilters (): MemberTeamsPanelFilters {
			return MEMBER_TEAMS_RESTING_FILTERS;
		},
		appliedPanelFilters (): MemberTeamsPanelFilters {
			return {
				...this.appliedFilters,
				activation: this.showingDeactivated ? TEAM_ACTIVATION.DEACTIVATED : TEAM_ACTIVATION.ACTIVE,
			};
		},
		// CursorListMixin
		listingReady (): boolean {
			return !!(this.currTeam?.companyId && this.currTeamMember?.email);
		},
		listingPending (): boolean {
			return !this.currTeam || !this.currTeamMember;
		},
		listFetchFailureMessages (): Record<string, string> {
			return {
				unauthorized: `Your session has expired, sign in again to view ${this.teamLabelPlural}`,
				forbidden: `You do not have access to this member's ${this.teamLabelPlural}`,
				network: 'Could not reach the server, check your connection and try again',
				failed: `Could not load ${this.teamLabelPlural}, please try again`,
			};
		},
		headers () {
			return [
				{
					text: this.displayLabels.team,
					value: TEAMS_HEADER_VALUE.NAME,
					align: 'left',
					class: 'py-0 pl-0 pr-1',
					sortable: true,
				},
				...(this.teamCustomFieldDefs.map((fieldDef: CompanyCustomFieldDef) => ({
					text: fieldDef.label,
					value: `${TEAM_CUSTOM_FIELD_HEADER_PREFIX}${fieldDef.key}`,
					align: 'left',
					class: 'py-0 px-1',
					sortable: true,
				}))),
				...(this.showTeamRoleColumn ? [
					{
						noDownload: true,
						text: `${this.displayLabels.team} Role`,
						value: TEAMS_HEADER_VALUE.TEAM_ROLE,
						align: 'left',
						class: 'py-0 px-1',
						sortable: false,
					},
				] : []),
				{
					noDownload: true,
					text: 'Joined',
					value: TEAMS_HEADER_VALUE.JOINED,
					align: 'left',
					class: 'py-0 px-1',
					sortable: false,
				},
				...(this.canBulkManageTeams ? [
					{
						noDownload: true,
						text: 'Actions',
						value: TEAMS_HEADER_VALUE.ACTIONS,
						align: 'right',
						class: 'py-0 pr-4',
						sortable: false,
					},
				] : []),
			];
		},
		customFieldKeys (): string[] {
			return this.teamCustomFieldDefs.map((fieldDef: CompanyCustomFieldDef) => fieldDef.key);
		},
		// The CSV is built from the Postgres rows alone, never a budget or a balance.
		downloadRows (): TeamDownloadRow[] {
			return this.selectedRows.map((row: TeamListRow) => teamDownloadRow(row));
		},
		downloadHeaders (): TableHeaderValue[] {
			return teamDownloadHeaders(this.selectedRows, this.displayLabels.team, [
				...(this.showTeamRoleColumn ? [{ text: `${this.displayLabels.team} Role`, value: 'teamRole' }] : []),
				{ text: 'Status', value: 'status' },
				{ text: 'Joined', value: 'joined' },
			]);
		},
		downloadSort (): { sortBy: string[]; sortDesc: boolean[] } {
			return teamTablesDownloadSort(this.options, [TEAMS_HEADER_VALUE.NAME]);
		},
		isNarrowed (): boolean {
			// Activation is not narrowing: it picks the half the title names
			return !!this.searchQuery || !!this.appliedFilters.status;
		},
		emptyStateMessage (): string {
			if (this.failed) return `Could not load ${this.teamLabelPlural}`;
			if (this.isNarrowed) return `No ${this.teamLabelPlural} match your search or filters`;
			if (this.showingDeactivated) return `This member is not on any deactivated ${this.teamLabelPlural}`;
			return `This member is not on any ${this.teamLabelPlural}`;
		},
	},
	watch: {
		// Another member (or the same member reached through another Team) is another listing.
		viewKey: {
			immediate: true,
			handler (newVal: string | null, oldVal: string | null) {
				if (newVal === oldVal) return;
				this.clearSelectedRows();
				this.walk = restartWalk();
				this.hydrateView(newVal);
				this.scheduleFetch();
			},
		},
		// Active or Deactivated changes with the route, Apply's or the browser's back and forward
		showingDeactivated (newVal: boolean, oldVal: boolean) {
			if (newVal === oldVal) return;
			this.clearSelectedRows();
			this.draftFilters = { ...this.draftFilters, activation: this.appliedPanelFilters.activation };
			this.resetToFirstPage();
		},
	},
	beforeDestroy () {
		if (this.currTeam?.id && this.currTeamMember?.id) {
			this.storeLastPagination({
				key: 'lastTeamMemberPagination',
				// With the half it was taken in, so a cursor is only resumed there
				pagination: rememberListView(this.options, this.searchQuery, { ...this.appliedFilters, activation: this.listedActivation }, this.walk.cursor, walkPageNumber(this.walk)),
				teamId: this.currTeam.id,
				teamMemberId: this.currTeamMember.id,
			});
		}
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'storeLastPagination',
		]),
		hydrateView (viewKey: string | null) {
			if (!viewKey || this.hydratedViewKey === viewKey) return;
			this.hydratedViewKey = viewKey;
			const stored: StoredLastPagination | undefined =
				this.companyDashboard?.lastTeamMemberPagination?.[this.currTeam.id]?.[this.currTeamMember.id];
			const view: HydratedListView<MemberTeamsPanelFilters> = hydrateListView(
				stored,
				TEAMS_DEFAULT_OPTIONS,
				MEMBER_TEAMS_RESTING_FILTERS,
				{
					isSortable: (header: string) => isSortableTeamsHeader(header, this.customFieldKeys),
					itemsPerPageOptions: TEAM_TABLES_ITEMS_PER_PAGE_OPTIONS,
				},
			);
			this.options = view.options;
			this.searchQuery = view.searchQuery || '';
			// Activation stays the route's: arriving from the deactivated Teams must land there
			this.appliedFilters = { ...(view.filters || MEMBER_TEAMS_RESTING_FILTERS), activation: MEMBER_TEAMS_RESTING_FILTERS.activation };
			// The position belongs to the half it was taken in, which the route may not be showing now
			const storedActivation: unknown = stored && stored.filters ? stored.filters.activation : undefined;
			const sameHalf: boolean = (storedActivation === TEAM_ACTIVATION.DEACTIVATED) === this.showingDeactivated;
			this.walk = sameHalf ? view.walk : restartWalk();
			this.syncFiltersPanel();
		},
		fetchListPage ({ idToken, cursor }: { idToken: string; cursor: string | null }): Promise<ListFetchResult<TeamListRow>> {
			this.listedActivation = this.appliedPanelFilters.activation;
			return fetchTeamsPage({
				baseUrl: TEAMS_API_BASE_URL,
				idToken,
				request: buildMemberTeamsRequest({
					companyId: this.currTeam?.companyId || null,
					accountTeamId: this.team?.id || null,
					memberEmail: this.currTeamMember?.email || null,
					cursor,
					options: this.options,
					searchQuery: this.searchQuery,
					filters: this.appliedPanelFilters,
				}),
			});
		},
		// A selected Team back on screen is selected as this copy, so the CSV reads the newer one.
		onListRowsShown (rows: TeamListRow[]) {
			const shownById: Record<string, TeamListRow> = _keyBy(rows, 'id');
			if (this.selectedRows.some((row: TeamListRow) => shownById[row.id] && shownById[row.id] !== row)) {
				this.selectedRows = this.selectedRows.map((row: TeamListRow) => shownById[row.id] || row);
			}
		},
		/**
		 * Apply and Clear all land here. Status is the table's; Activation is the
		 * route's, so a change there goes through the route, whose watcher starts
		 * the walk over.
		 */
		commitPanelFilters (next: MemberTeamsPanelFilters) {
			const clean: MemberTeamsPanelFilters = normalizePanelFilters(MEMBER_TEAMS_RESTING_FILTERS, next);
			this.clearSelectedRows();
			this.appliedFilters = { ...clean, activation: MEMBER_TEAMS_RESTING_FILTERS.activation };
			const wantDeactivated: boolean = clean.activation === TEAM_ACTIVATION.DEACTIVATED;
			if (wantDeactivated !== this.showingDeactivated) {
				this.routerReplace(this.$route, this.$router, {
					name: this.$route.name,
					params: this.$route.params,
					query: {
						..._omit(this.$route.query, 'deactivated'),
						...(wantDeactivated && { deactivated: '1' }),
					},
				});
				return;
			}
			this.resetToFirstPage();
		},
		setSearchQuery (query: string) {
			const next: string = query || '';
			if (next === this.searchQuery) return;
			this.clearSelectedRows();
			this.searchQuery = next;
			this.resetToFirstPage();
		},
		clearSelectedRows () {
			this.$nextTick(() => {
				// Next tick seems to prevent issue with check boxes getting in a state where they're unselectable after a bulk update and clear selected items operation
				this.selectedRows = [];
			});
		},
		stubFor (row: TeamListRow): Team {
			return stubTeamFromRow(
				row,
				this.currTeam?.companyId || null,
				(iso: string): DbTimestamp => Timestamp.fromMillis(moment(iso).valueOf()) as DbTimestamp,
			);
		},
		// A change made here: the count on the card above catches up once the mirror has it.
		onTeamsChanged () {
			this.clearSelectedRows();
			noteTeamsChanged();
		},
		// A row's membership, or its Team, is gone: never act on it, and ask again later.
		onRowRemoved (teamId: string) {
			this.selectedRows = this.selectedRows.filter((row: TeamListRow) => row.id !== teamId);
			this.refetchAfterMirrorLag();
		},
		/**
		 * A row's own action. The row may still be showing its Postgres stand-in, so the
		 * dialog gets the Team read fresh, as the bulk actions do.
		 */
		async onRowAction (eventName: string, payload: Record<string, unknown>) {
			let fresh: Record<string, unknown> | null;
			try {
				fresh = await withFreshTeams(payload);
			} catch (error) {
				console.error('Error loading the team:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Could not load this ${this.displayLabels.team}, please try again`,
					show: true,
				});
				return;
			}
			if (this.isDestroyed) return;
			if (!fresh) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `This ${this.displayLabels.team} is no longer available`,
					show: true,
				});
				return;
			}
			this.reemitWithRefetch(eventName, fresh);
		},
		reemitWithRefetch (eventName: string, payload: Record<string, unknown>) {
			this.$emit(eventName, {
				...payload,
				onSuccess: () => this.onTeamsChanged(),
			});
		},
		/**
		 * The bulk Roles and Remove edit the memberships on these Teams, so the
		 * dialogs get the Teams as they are now, read by id, rather than the
		 * mirror's rows, which can be a few seconds behind and may span pages.
		 */
		async onBulkTeams (eventName: 'edit-team-member-teams' | 'remove-member-from-teams') {
			if (!this.selectedRows.length || this.loadingBulkTeams) return;
			this.loadingBulkTeams = true;
			try {
				const teams: Team[] = await fetchTeamsById(this.selectedRows.map((row: TeamListRow) => row.id));
				if (this.isDestroyed) return;
				if (!teams.length) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `The selected ${this.teamLabelPlural} are no longer available`,
						show: true,
					});
					return;
				}
				this.reemitWithRefetch(eventName, { teams, teamMember: this.currTeamMember });
			} catch (error) {
				console.error('Error loading the selected teams:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Could not load the selected ${this.teamLabelPlural}, please try again`,
					show: true,
				});
			} finally {
				this.loadingBulkTeams = false;
			}
		},
	},
	components: {
		BaseButton,
		EmptyState,
		FiltersPanel,
		LiveDoc,
		TableBulkActionsPopover,
		TableToolbar,
		TeamMemberTeamRow,
	},
});
